import React from 'react';
import { cn } from '@/lib/utils';
import { useChat } from '../../context/ChatContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SendHorizontal } from 'lucide-react';
import { TypingIndicator } from '@/components/chat/typing-indicator';
import { DEAIChatMessage } from '@/components/chat/deai-chatbox-message';
import { ScrollAreaAuto } from '@/components/ui/scroll-area-auto';
import { MicButton } from '@/components/chat/deai-mic-button';

interface DEAIChatBoxProps {
  className?: string;
}

export const DEAIChatBox: React.FC<DEAIChatBoxProps> = ({ className }) => {
  const {
    chatMessages,
    messageInput,
    setMessageInput,
    sendTextMessage,
    sendAudioMessage,
  } = useChat();

  const handleRecordingError = (error: Error) => {
    console.error("MicButton reported an error:", error);
  };

  return (
      <div className={cn("flex flex-col h-full", className)}>
        <div className="flex-grow overflow-y-auto mb-4">
          <ScrollAreaAuto className="h-full w-full" autoScrollToBottom={true}>
            <div className="flex flex-col gap-y-4 p-4">
              {chatMessages.map((item) => (
                <DEAIChatMessage
                  key={item.id}
                  variant={item.role}
                >
                  {item.content}
                </DEAIChatMessage>
              ))}
              <TypingIndicator isTyping={false} />
            </div>
          </ScrollAreaAuto>
        </div>

        <div className="shrink-0 flex flex-col gap-3 p-4">
          <div className="flex gap-3">
            <Input
              className='h-16 md:h-16 flex-grow rounded-[3em] p-4 md:p-8 text-sm md:text-base text-black outline-none ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-white/60 border-3 border-white'
              placeholder="Type a message..."
              value={messageInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMessageInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') sendTextMessage();
              }}
            />
            <Button
              className="rounded-full w-16 h-16"
              onClick={sendTextMessage}
              disabled={messageInput.trim() === ""}
            >
              <SendHorizontal className="size-7" />
            </Button>
          </div>
          <MicButton
            onRecordingComplete={sendAudioMessage}
            onError={handleRecordingError}
            label="HOLD TO SPEAK"
            className="w-full h-16 text-lg font-semibold" 
            onRecordingStarted={()=>{
              console.log("onRecordingStarted");
            }} 
            onRecordingEnded={()=>{
              console.log("onRecordingEnded");
            }}
            />
          <p className="text-center text-xs text-gray-400">
            ⚠ AIs can make mistakes! Please be patient.
          </p>
        </div>
      </div>
  );
};
